import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { DefaultButton, FontIcon, MessageBarType, Separator, Spinner, Text } from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// components
import ContractCommissionAgentFormComponent from './ContractCommissionAgent.Form.Component';
import { MessageComponent } from '../../../commons/component/MessageComponent';

// models
import { ContractCommissionAgent } from '../../../models/Entities/Contracts/ContractCommissionAgent';
import {
  CommissionAgentTypeEnum,
  CommissionAgentTypeOptions,
  IsPractitionerAgent
} from '../../../models/Entities/Contracts/CommissionAgentType';

// functions
import * as ComponentFunctions from './ContractCommissionAgents.Functions';

// styles
import './ContractCommissionAgents.Styles.css';

const ContractCommissionAgentsComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingAgent, setEditingAgent] = useState<ContractCommissionAgent | undefined>(undefined);

  useEffect(() => {
    props.GetAllContractCommissionAgents(props.ContractId);
  }, [props.ContractId]);

  const handleAdd = () => {
    setEditingAgent(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (agent: ContractCommissionAgent) => {
    setEditingAgent(agent);
    setIsFormOpen(true);
  };

  const TypeLabel = (type: CommissionAgentTypeEnum | undefined | null): string => {
    const option = CommissionAgentTypeOptions.find(
      (item) => item.key === (type ?? CommissionAgentTypeEnum.Practitioner)
    );
    return option ? t(option.textKey, { defaultValue: option.defaultText }) : '';
  };

  const CommissionLabel = (agent: ContractCommissionAgent): string =>
    agent.isCommissionAPercentage
      ? `${agent.commissionAmount}%`
      : `${agent.commissionAmount} ${agent.currency || ''}`.trim();

  return (
    <div className="cca-section">
      <Separator />

      <div className="cca-section__header">
        <div className="cca-section__title">
          <FontIcon iconName="Teamwork" className="cca-section__title-icon" />
          <Text variant="mediumPlus">
            {t('contractCommissionAgents.title', { defaultValue: 'Otros colaboradores' })}
          </Text>
          <Text variant="small" className="cca-section__subtitle">
            {t('contractCommissionAgents.subtitle', {
              defaultValue: 'Quienes comisionan sin formar parte del equipo del protocolo'
            })}
          </Text>
        </div>
        <DefaultButton
          iconProps={ComponentFunctions.AddIcon}
          text={t('contractCommissionAgents.add', { defaultValue: 'Agregar colaborador' })}
          onClick={handleAdd}
        />
      </div>

      {/* El alcance de esta sección, dicho antes de que alguien cargue a alguien:
          lo que se da de alta acá es una relación administrativa del contrato. No
          es una incorporación al protocolo y no habilita nada del lado médico
          —eso se administra en el protocolo, no en el contrato—. Sin aclararlo, la
          palabra "colaborador" al lado del equipo del protocolo invita justo a esa
          confusión. */}
      <MessageComponent
        message={t('contractCommissionAgents.notice', {
          defaultValue:
            'Esto es sólo a los efectos de la administración: no otorga ni modifica los accesos al protocolo en la parte médica.'
        })}
        type={MessageBarType.info}
      />

      {props.isLoading && (
        <div className="cca-section__loading">
          <Spinner
            label={t('contractCommissionAgents.loading', { defaultValue: 'Cargando colaboradores...' })}
            ariaLive="assertive"
            labelPosition="right"
          />
        </div>
      )}

      {!props.isLoading && props.Agents.length === 0 && (
        <div className="cca-section__empty">
          <FontIcon iconName="Teamwork" className="cca-section__empty-icon" />
          <p>
            {t('contractCommissionAgents.empty', {
              defaultValue: 'Este contrato no tiene colaboradores fuera del equipo del protocolo'
            })}
          </p>
        </div>
      )}

      {props.Agents.length > 0 && (
        <div className="cca-section__grid">
          {props.Agents.map((agent: ContractCommissionAgent) => {
            const isPractitioner = IsPractitionerAgent(agent.commissionAgentType);

            return (
              /* La tarjeta entera abre el panel, igual que en el equipo del
                 protocolo: ahí adentro están los conceptos a liquidar, que es a
                 lo que se entra a hacer. Un lapicito escondido en una esquina no
                 lo sugiere, y además obligaba a apuntar a un blanco de 32px. */
              <button
                key={'CCA_' + agent.id}
                type="button"
                className="cca-card-wrapper"
                onClick={() => handleEdit(agent)}
                title={t('contractCommissionAgents.edit', { defaultValue: 'Modificar' })}
              >
                <div className="cca-card">
                  {/* El acento distingue de un vistazo al comisionista médico del
                      que no lo es, que es justamente lo que esta sección vino a
                      poder representar. */}
                  <div
                    className={
                      'cca-card__accent' +
                      (isPractitioner ? ' cca-card__accent--practitioner' : '')
                    }
                  />
                  <div className="cca-card__body">
                    <div className="cca-card__header">
                      <span className="cca-card__name">
                        {agent.commissionAgentName ||
                          t('contractCommissionAgents.unnamed', { defaultValue: 'Sin nombre' })}
                      </span>
                      <div className="cca-card__header-right">
                        <span
                          className={
                            'cca-card__badge' +
                            (isPractitioner ? ' cca-card__badge--practitioner' : '')
                          }
                        >
                          {TypeLabel(agent.commissionAgentType)}
                        </span>
                        <FontIcon iconName="Settings" className="cca-card__settings-hint" />
                      </div>
                    </div>

                    <div className="cca-card__meta">
                      <span className="cca-card__meta-label">
                        {t('contractCommissionAgents.commission', { defaultValue: 'Comisión' })}:
                      </span>
                      <strong>{CommissionLabel(agent)}</strong>
                      {agent.onlyDoneHimSelf && (
                        <span className="cca-card__flag">
                          {' · '}
                          {t('contractCommissionAgents.onlyDoneHimSelf', {
                            defaultValue: 'Sólo lo propio'
                          })}
                        </span>
                      )}
                    </div>

                    {/* Aparece cuando la tarjeta crece: le da sentido a la
                        animación y dice a qué se entra. */}
                    <div className="cca-card__reveal">
                      {t('contractCommissionAgents.openHint', {
                        defaultValue: 'Abrir para configurar los conceptos a liquidar'
                      })}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <ContractCommissionAgentFormComponent
        ContractId={props.ContractId}
        Agent={editingAgent}
        isOpen={isFormOpen}
        dismissPanel={() => {
          setIsFormOpen(false);
          setEditingAgent(undefined);
        }}
      />
    </div>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ContractCommissionAgentsComponent as any);
